import { useLayoutEffect, useRef, useState } from "react";

import Typography from "@/design-system/Typography";
import Button from "@/design-system/button";
import ArrowIcon from "@/design-system/icons/arrow-icon";
import { sendTelemetryEvent } from "@/lib/telemetry";
import { useViewportInfoContext } from "@/src/contexts";
import type { IStepper } from "@Components/Stepper/Stepper";
import Stepper from "@Components/Stepper/Stepper";
import * as stylex from "@stylexjs/stylex";

import { ComposableSpacing, type QuerySmMax, colors } from "../../../styles/tokens.stylex";
import { getLineInfo, getResources, stepperSteps } from "./CashbackProcessStepsData";

export function CashbackProcessSteps() {
    const { isMobileOrTablet } = useViewportInfoContext();

    const [isCollapsed, setIsCollapsed] = useState(isMobileOrTablet);
    const refTopBanner = useRef<HTMLDivElement>(null);
    const refStepsBanner = useRef<HTMLDivElement>(null);
    const prevIsCollapsedRef = useRef(isCollapsed);

    const maxHeight = isMobileOrTablet ? "60px" : undefined;

    useLayoutEffect(() => {
        if (prevIsCollapsedRef.current !== isCollapsed) {
            prevIsCollapsedRef.current = isCollapsed;
            refTopBanner.current?.animate(
                {
                    maxHeight: [
                        `${
                            (refStepsBanner.current?.firstElementChild?.clientHeight || 0) +
                            (isMobileOrTablet ? 22 : 84)
                        }px`,
                        maxHeight || "145px",
                    ],
                } as PropertyIndexedKeyframes,
                {
                    direction: isCollapsed ? "normal" : "reverse",
                    duration: 300,
                    fill: "forwards",
                    easing: "linear",
                },
            );
        }
    }, [isCollapsed, isMobileOrTablet, maxHeight]);

    const updateCollapsed = () => {
        sendTelemetryEvent({
            eventName: "cshbck-bannerClick",
            actionType: "Click",
            itemId: isCollapsed ? "1" : "0",
        });

        setIsCollapsed((currentState) => !currentState);
    };

    const resources = getResources();

    const steps: IStepper = {
        stepList: stepperSteps(resources.stepper),
        lineInfo: getLineInfo(isMobileOrTablet),
        lineColor: "#1B2670",
    };

    const icon = <ArrowIcon direction={isCollapsed ? "down" : "up"} styleX={styles.arrow} />;

    return (
        <div
            {...stylex.props(styles.cashbackProcessStepsComponent)}
            ref={refTopBanner}
            style={{ maxHeight: maxHeight }}
        >
            {isMobileOrTablet && (
                <Button
                    as="button"
                    variant="transparent"
                    size="None"
                    onClick={updateCollapsed}
                    styleX={styles.header}
                >
                    <Typography fontStyle="hXS" styleX={styles.headerTitleMobile}>
                        {resources.mobilesTitle}
                    </Typography>
                    {icon}
                </Button>
            )}
            <div {...stylex.props(styles.stepWrapper)} ref={refStepsBanner}>
                <Stepper {...steps} />
            </div>
            {!isMobileOrTablet && (
                <div {...stylex.props(styles.buttonWrapper)}>
                    <Button
                        as="button"
                        variant="transparent"
                        size="None"
                        onClick={updateCollapsed}
                        icon={icon}
                        styleX={styles.button}
                    >
                        {isCollapsed ? resources.buttonTextOpen : resources.buttonTextClose}
                    </Button>
                </div>
            )}
        </div>
    );
}

const smMax: QuerySmMax = "@media (max-width: 989px)";

const styles = stylex.create({
    cashbackProcessStepsComponent: {
        color: colors.bg_Primary,
        display: "flex",

        flexDirection: {
            default: "row",
            [smMax]: "column",
        },
    },

    stepWrapper: {
        overflow: "hidden",
    },

    header: {
        color: colors.b_Primary,
        display: "flex",
        fontSize: {
            default: "24px",
            [smMax]: "18px",
        },
        justifyContent: "space-between",

        lineHeight: {
            default: "28px",
            [smMax]: "24px",
        },

        paddingBottom: {
            default: "20px",
            [smMax]: "18px",
        },
        paddingLeft: {
            default: 0,
            [smMax]: 0,
        },
        paddingRight: {
            default: 0,
            [smMax]: 0,
        },
        paddingTop: {
            default: "20px",
            [smMax]: "18px",
        },
        width: "100%",
    },

    headerTitleMobile: {
        color: colors.b_Primary,
        fontWeight: 700,
    },

    arrow: {
        strokeWidth: 4,
        flexGrow: 0,
        transitionDuration: "180ms",
        transitionProperty: "transform",
        transitionTimingFunction: "ease-out",
        height: 24,
        width: 24,
    },

    buttonWrapper: {
        display: "block",
        position: "absolute",
        bottom: 0,
        right: 0,
    },

    button: {
        backgroundColor: colors.fg_AltDefault,

        boxShadow: "0px 3px 20px rgba(0, 0, 0, 0.1)",
        fontSize: "1.125rem",
        lineHeight: 1,

        transform: "translateY(100%)",
        borderBottomLeftRadius: ComposableSpacing.s3,
        borderBottomRightRadius: ComposableSpacing.s3,
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        paddingBottom: "10px",
        paddingLeft: "40px",
        paddingRight: "40px",
        paddingTop: "10px",
    },
});
